import { useCallback, useEffect, useMemo, useReducer } from 'react'

import { ButtonDefault, ButtonText } from '@/components/common/Button'
import { InputSelect } from '@/components/common/Input'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { useIsNavigating } from '@/components/providers/NavigationProvider'
import { usePage } from '@/components/providers/PageProvider'
import {
  FILTER_ADVANCED_MAX,
  FILTER_ADVANCED_SLUG_LABELS,
  type FilterAdvancedPartialType,
  type FilterAdvancedSlugType,
  type FilterAdvancedType,
  isFilterAdvancedType,
  withFilterAdvancedDefaults,
} from '@/defs/filters'
import { type DropdownOptionGroupType } from '@/defs/input'
import { SCROLL_GALLERY } from '@/defs/scroll'
import { useClickaway } from '@/hooks/useClickaway'
import { useFiltersFocus } from '@/hooks/useFiltersFocus'
import { useLinkInternal } from '@/hooks/useLinkInternal'
import { trackEvent } from '@/utils/analyticsUtil'
import { getClickawayIgnoreProps } from '@/utils/clickawayUtil'
import { cn } from '@/utils/commonUtil'
import { buildAdvancedRoute, cleanAdvancedFilters } from '@/utils/routeUtil'

import styles from './FiltersPanelAdvanced.module.scss'
import { FiltersPanelAdvancedForm } from './FiltersPanelAdvancedForm/FiltersPanelAdvancedForm'
import { FiltersPanelAdvancedItem } from './FiltersPanelAdvancedItem/FiltersPanelAdvancedItem'

type AdvancedFilterFormType =
  (FilterAdvancedType & { isEditing: false }) | (FilterAdvancedPartialType & { isEditing: true })

type StateType = {
  advanced: AdvancedFilterFormType[]
  isEditMode: boolean
}

type ActionType =
  | {
      action: 'close'
      advanced?: FilterAdvancedType[]
    }
  | {
      action: 'add'
      slug: FilterAdvancedSlugType
    }
  | {
      action: 'edit' | 'delete'
      index: number
    }
  | {
      action: 'updateFilter'
      filter: FilterAdvancedPartialType
      index: number
    }
  | {
      action: 'submitFilter'
      filter: FilterAdvancedType
      index: number
    }

const toFormFilters = (filters: FilterAdvancedType[]): AdvancedFilterFormType[] =>
  filters.map((filter) => ({ ...filter, isEditing: false }))

const initState = (filters: FilterAdvancedType[]): StateType => ({
  advanced: toFormFilters(filters),
  isEditMode: false,
})

const makeReducer =
  (filtersAdvanced: FilterAdvancedType[]) =>
  (state: StateType, action: ActionType): StateType => {
    const advanced = state.isEditMode ? [...state.advanced] : toFormFilters(filtersAdvanced)

    switch (action.action) {
      case 'add': {
        const filterNew: AdvancedFilterFormType = {
          ...withFilterAdvancedDefaults(action.slug),
          isEditing: true,
        }
        return {
          advanced: [...advanced, filterNew],
          isEditMode: true,
        }
      }
      case 'edit': {
        const advancedFilter = advanced[action.index]
        if (advancedFilter) advanced[action.index] = { ...advancedFilter, isEditing: true }
        return {
          advanced,
          isEditMode: true,
        }
      }
      case 'delete': {
        return {
          advanced: advanced.filter((_, c) => c !== action.index),
          isEditMode: true,
        }
      }
      case 'updateFilter': {
        if (!state.isEditMode) return state
        if (advanced[action.index]) advanced[action.index] = { ...action.filter, isEditing: true }
        return {
          advanced,
          isEditMode: true,
        }
      }
      case 'submitFilter': {
        if (!state.isEditMode) return state
        if (advanced[action.index]) advanced[action.index] = { ...action.filter, isEditing: false }
        return {
          advanced,
          isEditMode: true,
        }
      }
      case 'close': {
        return initState(action.advanced || filtersAdvanced)
      }
    }
  }

export const FiltersPanelAdvanced = () => {
  const { page } = usePage()
  const isNavigating = useIsNavigating()
  const { redirectRelative } = useLinkInternal()
  const route = page?.route
  const advancedFilters = useMemo(() => route?.advancedFilters || [], [route?.advancedFilters])
  const [{ advanced, isEditMode }, dispatch] = useReducer(makeReducer(advancedFilters), advancedFilters, initState)
  const advancedOptions = page?.optionsFilters?.advanced
  const isFocusRequested = useFiltersFocus('advanced')

  useEffect(() => {
    if (!isNavigating) dispatch({ action: 'close', advanced: advancedFilters })
  }, [advancedFilters, isNavigating])

  const optionGroups: DropdownOptionGroupType<FilterAdvancedSlugType>[] = useMemo(
    () =>
      advancedOptions?.map((group) => ({
        options: group.map((value) => ({
          label: (
            <IconWithLabel iconBefore={FILTER_ADVANCED_SLUG_LABELS[value].icon}>
              {FILTER_ADVANCED_SLUG_LABELS[value].label}
            </IconWithLabel>
          ),
          value,
        })),
      })) || [],
    [advancedOptions],
  )

  const onApply = useCallback(() => {
    const advancedNew = cleanAdvancedFilters(advanced) || []
    if (buildAdvancedRoute(advancedNew) === buildAdvancedRoute(advancedFilters)) {
      dispatch({ action: 'close' })
      return
    }
    redirectRelative({ advancedFilters: advancedNew }, { scroll: SCROLL_GALLERY })
    trackEvent({ action: 'card_filters_submit', category: 'card_filters', label: `${advanced.length}` })
    dispatch({ action: 'close', advanced: advancedNew })
  }, [advanced, advancedFilters, redirectRelative])

  useClickaway({
    ignore: 'filter-form',
    onClickAway: () => {
      if (saveEnabled && !isNavigating) onApply()
    },
  })

  const showAdd = isEditMode
    ? advanced.length < FILTER_ADVANCED_MAX
    : (advancedFilters.length || 0) < FILTER_ADVANCED_MAX
  const saveEnabled = isEditMode && advanced.every((f) => isFilterAdvancedType(f))

  if (!advancedOptions) return null

  return (
    <div className={cn(styles.container, { isEditMode, styles })}>
      {isEditMode && advanced.length < 1 && <p className={styles.emptyMessage}>No advanced filters set.</p>}
      <ul className={styles.list}>
        {advanced.map((filter, index) => (
          <li key={`${filter.slug}-${index}`}>
            {filter.isEditing ? (
              <FiltersPanelAdvancedForm
                filter={filter}
                onDelete={() => dispatch({ action: 'delete', index })}
                onSubmit={(filter) => dispatch({ action: 'submitFilter', filter, index })}
                onUpdate={(filter) => dispatch({ action: 'updateFilter', filter, index })}
                optionGroups={optionGroups}
              />
            ) : (
              <FiltersPanelAdvancedItem
                filter={filter}
                onDelete={() => dispatch({ action: 'delete', index })}
                onEdit={() => dispatch({ action: 'edit', index })}
              />
            )}
          </li>
        ))}
        {showAdd && (
          <li>
            <InputSelect
              autoFocus={isFocusRequested}
              onSelect={(slug) => dispatch({ action: 'add', slug })}
              optionGroups={optionGroups}
              width='maxcontent'
            >
              <ButtonDefault as='div' {...getClickawayIgnoreProps(['filter-form'])} disabled={isNavigating}>
                <IconWithLabel iconAfter='plus'>Add</IconWithLabel>
              </ButtonDefault>
            </InputSelect>
          </li>
        )}
      </ul>
      {isEditMode && (
        <div className={styles.actions}>
          <ButtonText
            hover='warning'
            onClick={() => dispatch({ action: 'close' })}
            {...getClickawayIgnoreProps(['filter-form'])}
          >
            Cancel
          </ButtonText>
          <ButtonDefault
            disabled={!saveEnabled}
            onClick={onApply}
            {...(saveEnabled ? getClickawayIgnoreProps(['filter-form']) : {})}
          >
            Apply Changes
          </ButtonDefault>
        </div>
      )}
    </div>
  )
}
